import {
  dateTimeToISO,
  getLanguageISO
} from 'application/src/js/functions.js';

import {
  blankBase64Image,
  dedereCompanyIds
} from 'application/src/js/globals.js';

import {
  FlyoutMenu
} from 'application/src/js/util';

/**
 * @class
 * @description class for displaying a dynamic offer
 * @param {Object} args.parent the parent class
 * @param {Node} args.element the outer element of the dynamic offer
 * @param {Object} args.data the JSON data of the dynamic offer
 * @param {Object} args.translations text translations object
 * @param {Object} args.user user JSON data
 * @param {boolean} [args.placeOfferDisabled=false] if true, the offer cannot be ordered here
 */
export default class DynamicOffer {
  constructor(args) {
    this.parent = args.parent;
    this.element = args.element;
    this.data = args.data;
    this.templateVersion = args && typeof args.templateVersion === 'string' ? args.templateVersion : 1;
    this.translations = translations.tradeItems;
    this.orderDisabled = typeof args.orderDisabled === 'undefined' ? false : args.orderDisabled;

    this.user = args.user;

    this.render();
  }

  render() {
    this.element.classList.add('trade-item');
    this.element.classList.add(`tpl-${this.templateVersion}`);
    this.element.innerHTML = this.template;

    if (this.element.querySelector('.flyout-menu')) {
      new FlyoutMenu({
        element: this.element.querySelector('.flyout-menu')
      })
    }
    // replace image source when loaded
    const img = new Image();
    img.src = this.imageSource;
    img.addEventListener('load', _ => [...this.element.querySelectorAll('.item-image img')].map(i => i.src = img.src));
  }

  get title() {
    return `${this.data.typename ? this.data.basicname + `, ` + this.data.typename : this.data.basicname}`;
  }

  get type() {
    if (this.data.dynamic_offer_id || this.data.type == 'dynamic') return this.translations.dynamicOffer;
    if (this.data.contract_offer_id || this.data.type == 'contract') return this.translations.contractOffer;
    return '';
  }

  get offerTypeName() {
    if (this.data.dynamic_offer_id) return 'dynamic';
    if (this.data.contract_offer_id) return 'contract';
  }

  get isInGroup() {
    return this.data.dynamic_offer_id ? !!this.data.dynamic_offer_group_id : !!this.data.contract_offer_group_id;
  }

  get isOwnOffer() {
    return this.user && this.data.created_id === this.user.company_user_id;
  }

  get targetUrl() {
    if (this.data.dynamic_offer_id) return `/mundus-agri/offer/details/dynamic/${this.data.dynamic_offer_id}`;
    if (this.data.contract_offer_id) return `/mundus-agri/offer/details/contract/${this.data.contract_offer_id}`;
  }

  get canAccessDetails() {
    return this.data.company_name.indexOf('* *') === -1;
  }

  get company() {
    if (this.canAccessDetails) {
      return this.data.global_address_id ? `
        <a title="${this.translations.goToCompany}" href="/mundus-agri/addressDetails/${this.data.global_address_id}">
          ${this.data.company_name}
        </a>
      ` : `${this.data.company_name}`;
    } else {
      return `<span class="no-access">${this.data.company_name}</span>`
    }
  }

  get imageSource() {
    // some implementations already have the imagepath in the response data
    if (this.data.imagepath) return this.data.imagepath;

    // use type image if available
    if (this.data.basic_type_image && this.data.basic_type_image_extension) {
      return `/images/product_images/type/medium/${this.data.basic_type_image}.${this.data.basic_type_image_extension}`
    }

    // use basic image if available
    if (this.data.basic_image && this.data.basic_image_extension) {
      return `/images/product_images/basic/medium/${this.data.basic_image}.${this.data.basic_image_extension}`
    }

    // use default image
    return `/projects/application/dist/images/no-image.png`;
  }

  get image() {
    return `
      <div class="item-image b-lazy" data-src="${this.imageSource}">
        ${!this.data.deleted ? `<a title="${this.translations.goToOffer}" href="${this.targetUrl}">` : `<div>`}
          <img src="${blankBase64Image}" alt="${this.title}">

          ${this.data.is_bio ? `
            <div class="bio-icon">
              <i class="fas fa-leaf"></i>
              <span>${this.translations.bio}</span>
            </div>
          ` : ''}

          ${!this.data.deleted ? `
            <market-item-countdown small time="${this.data.valid_until}"></market-item-countdown>
          ` : `<div class="deleted-info">${this.translations.deletedOfferInfo}</div>`}

          ${this.data.has_group ? `
            <business-group-icon
              class="absolute top-right"
              is-in-group="${this.data.dynamic_offer_group_id ? 1 : 0}"
              is-own="${this.user && this.data.global_address_id == this.user.global_address_id ? 1 : 0}">
            </business-group-icon>
          ` : ''}
        ${!this.data.deleted ? `</a>` : `</div>`}
      </div>
    `;
  }

  get isOwnCompanyItem() {
    return this.user && this.data.company_id === this.user.company_id;
  }

  get isDedereUser() {
    return this.user && dedereCompanyIds.includes(this.user.company_id);
  }

  get canPlaceOrder() {
    return !this.orderDisabled &&
      !!this.data.smartcontract &&
      !this.isOwnCompanyItem &&
      ((this.data.has_group && this.isInGroup) || !this.data.has_group);
  }

  get goToOfferButtonTemplate() {
    return `
      <a title="${this.translations.goToOffer}" class="button expanded" href="${this.targetUrl}">
        ${this.translations.goToOffer}
      </a>
    `;
  }

  get itemId() {
    return this.data.dynamic_offer_id || this.data.contract_offer_id;
  }

  get template() {
    this.data.quantity_per_unit = this.data.quantity_per_unit || 1;

    return `
      <offer-ribbon type="${this.data.highlighting_type || ''}" version="2"></offer-ribbon>
      <div class="item-left">

        <div class="item-top">
          <div class="itl">
            <div>
              <div class="item-date">
                ${this.data.last_edit_date ?
        new Intl.DateTimeFormat(getLanguageISO(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(new Date(dateTimeToISO(this.data.last_edit_date)))
        : ''}
              </div>
              <div class="item-type">
                ${this.type}
              </div>
            </div>
            <div>
              <div class="item-title">
                ${!this.data.deleted ? `
                  <a title="${this.translations.goToOffer}" href="${this.targetUrl}">
                    ${this.title}
                  </a>
                ` : this.title}
              </div>

              ${this.isOwnCompanyItem ? `
                <div class="flyout-menu">
                  <a class="flyout-trigger"><i class="fas fa-ellipsis-v"></i></a>
                  <ul>
                    <li>
                      <a href="/incursio/marketEdit/${this.data.offer_type}/${this.itemId}">
                        ${this.translations.edit}
                      </a>
                    </li>

                    ${this.isDedereUser && this.offerTypeName === 'dynamic' ? `
                      <li>
                        <a href="/incursio/createSmartContract/0/${this.itemId}">
                          ${this.translations.createSmartContract}
                        </a>
                      </li>
                    ` : ''}

                    ${this.data.global_product_basic_id ? `
                      <li>
                        <a href="/mundus-agri/offer/createNgmailing/${this.data.global_product_basic_id}/${this.itemId}/${this.offerTypeName}">
                          ${this.translations.send}
                        </a>
                      </li>
                    ` : ''}
                  </ul>
                </div>
              ` : ''}

            </div>
          </div>
          ${this.image}
        </div>

        <div class="item-details">
          ${this.data.categoryname ? `
            <div class="item-row">
              <div class="item-column">${this.translations.category}</div>
              <div class="item-column">${this.data.categoryname} / ${this.data.sub_categoryname}</div>
            </div>
          ` : ''}

          ${this.data.company_name ? `
            <div class="item-row">
              <div class="item-column">${this.translations.supplier}</div>
              <div class="item-column">
                ${this.canAccessDetails && this.data.client_global_address_name ? `
                  <tool-tip title="${this.translations.vendor}: ${encodeURIComponent(`<a href="/mundus-agri/addressDetails/${this.data.client_global_address_id}">${this.data.client_global_address_name}</a>`)}">
                    <i class="fas fa-info-circle"></i>
                  </tool-tip>
                ` : ''}

                ${this.company}

                ${this.data.ratings ? `
                  <company-rating
                    address-id="${this.data.global_address_id}"
                    amount="${this.data.ratings.amount}"
                    criteria="${btoa(JSON.stringify(this.data.ratings.criteria))}"
                    has-access="${this.user.useratingfeature}"
                    is-company-admin="${this.user.is_company_admin ? 1 : 0}"
                    is-rating-admin="${this.user.is_rating_admin ? 1 : 0}"
                    is-logged-in="${this.user ? 1 : 0}"
                    own-address-id="${this.user.global_address_id}"
                    rating="${this.data.ratings.avr_scoring_descr}"
                    score="${this.data.ratings.avr_scoring}"
                    hide-empty="1"
                    class="compact small">
                  </company-rating>
                ` : ''}
              </div>
            </div>
          ` : `
            ${this.idDedereUser && this.data.client_global_address_name ? `
              <div class="item-row">
                <div class="item-column">${this.translations.vendor}</div>
                <div class="item-column">
                  <a href="/mundus-agri/addressDetails/${this.data.client_global_address_id}">${this.data.client_global_address_name}</a>
                </div>
              </div>
            ` : ''}
          `}

          ${this.data.freight_terms ? `
            <div class="item-row">
              <div class="item-column">${this.translations.incoType}</div>
              <div class="item-column">${this.data.freight_terms}</div>
            </div>
          ` : ''}

          ${this.data.package_name ? `
            <div class="item-row">
              <div class="item-column">${this.translations.packaging}</div>
              <div class="item-column">${this.data.package_name}</div>
            </div>
          ` : ''}

          ${(this.data.has_group && this.isInGroup) || this.isOwnOffer || !this.data.has_group ? `
            ${this.offerTypeName === 'dynamic' && this.data.quantity_per_unit ? `
              <div class="item-row">
                <div class="item-column"><a class="open-modal-logistic-info">${this.translations.deliveryUnit}</a></div>
                <div class="item-column">
                  ${this.data.quantity_per_unit} <span class="times"></span> ${this.data.package_name}
                  (${new Intl.NumberFormat(getLanguageISO()).format(this.data.quantity_per_unit * this.data.package_weight)} ${this.data.measurement})
                </div>
              </div>
            ` : ''}

            ${this.data.min_order_quantity ? `
              <div class="item-row">
                <div class="item-column">${this.translations.minOrderQuantity}</div>
                <div class="item-column">
                  ${Math.floor(this.data.min_order_quantity / this.data.quantity_per_unit / parseFloat(this.data.package_weight))}
                  ${Math.floor(this.data.min_order_quantity / this.data.quantity_per_unit / parseFloat(this.data.package_weight)) != 1 ? this.translations.deliveryUnits : this.translations.deliveryUnit}
                  (${new Intl.NumberFormat(getLanguageISO()).format(
          Math.floor(this.data.min_order_quantity / this.data.quantity_per_unit / parseFloat(this.data.package_weight))
          * (this.data.quantity_per_unit * parseFloat(this.data.package_weight))
        )} ${this.data.measurement})
                </div>
              </div>
            ` : ''}
          ` : ''}

          <div class="item-row">
            <div class="item-column">${this.translations.price}</div>
            <div class="item-column price-out">
              ${this.user && this.data.has_group && !this.isInGroup && !this.isOwnOffer ? `
                <join-business-group-button
                  offer-type="dynamic"
                  offer-id="${this.data.dynamic_offer_id}"
                  size="tiny">
                </join-business-group-button>
              ` : (this.data.price_formatted || this.data.price)}
            </div>
          </div>

          ${this.data.valid_until ? `
            <div class="item-row">
              <div class="item-column">${this.translations.validUntil}</div>
              <div class="item-column">
                ${new Intl.DateTimeFormat(getLanguageISO(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(new Date(dateTimeToISO(this.data.valid_until)))}
              </div>
            </div>
          ` : ''}

        </div>
      </div>
      <div class="item-right">
        ${this.image}

        ${!this.data.deleted ? `
          ${this.canPlaceOrder ? `
            <place-order
              id="${this.data.dynamic_offer_id}"
              type="dynamic"
              quantity-per-unit="${this.data.quantity_per_unit}"
              package-weight="${this.data.package_weight}"
              measurement="${this.data.measurement}"
              min-order-quantity="${this.data.min_order_quantity}"
              total-units="${this.data.available_quantity}"
              price-display-option="${this.data.price_display_option}"
              class="break expanded">
            </place-order>
          ` : this.goToOfferButtonTemplate}
        ` : ''}
      </div>
      <div class="item-footer">
        ${!this.data.deleted ? `
          ${this.canPlaceOrder ? `
            <place-order
              id="${this.data.dynamic_offer_id}"
              type="dynamic"
              quantity-per-unit="${this.data.quantity_per_unit}"
              package-weight="${this.data.package_weight}"
              measurement="${this.data.measurement}"
              min-order-quantity="${this.data.min_order_quantity}"
              total-units="${this.data.available_quantity}"
              price-display-option="${this.data.price_display_option}">
            </place-order>
          ` : this.goToOfferButtonTemplate}
        ` : ''}
      </div>
    `;
  }
}